import {CurrencyPipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {Integration} from '@gatewee/types/integration';
import {Pool} from '@gatewee/types/pool';
import {
  AssociateWalletPayload,
  CreateWalletPayload,
  Wallet,
  walletBindingScope,
  walletProvider,
} from '@gatewee/types/wallet';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {WalletFormComponent} from './form/wallet-form.component';
import {IntegrationRoutingRuleActions} from './integration-routing-rules/integration-routing-rules.component';
import {WalletViewComponent} from './view/wallet-view.component';

export interface WalletActions {
  fetchWallets: (entityId: number) => Observable<Wallet[]>;
  createWallet: (payload: CreateWalletPayload) => Observable<Wallet>;
  syncWallet: (walletId: number) => Observable<Wallet>;
  setPrimary: (walletId: number) => Observable<Wallet>;
  fetchIntegrations: (
    entityId: number,
    inUse?: boolean,
  ) => Observable<Integration[]>;
  fetchPools: () => Observable<Pool[]>;
  integrationRouting?: IntegrationRoutingRuleActions;
  associateIntegration?: (
    walletId: number,
    payload: AssociateWalletPayload,
  ) => Observable<Wallet>;
}

@Component({
  selector: 'app-gatewee-wallets',
  templateUrl: './gatewee-wallets.component.html',
  styleUrls: ['./gatewee-wallets.component.css'],
  standalone: false,
  providers: [CurrencyPipe],
})
export class GateweeWalletsComponent implements OnInit, AfterViewInit {
  readonly walletProvider = walletProvider;
  readonly walletBindingScope = walletBindingScope;

  @Input('id') id!: number;
  @Input() actions!: WalletActions;
  @Input() disableContentPage = false;
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() title = 'features.wallets.title';
  @Input() titlePath = 'features.wallets.titlePath';

  wallets: Wallet[] = [];
  loadingWallets = false;
  walletSyncingId: number | null = null;
  walletColumns: GateweeTableColumn[] = [];

  @ViewChild('walletBindingTpl', {read: TemplateRef})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  walletBindingTpl!: TemplateRef<any>;

  @ViewChild('walletAccountTpl', {read: TemplateRef})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  walletAccountTpl!: TemplateRef<any>;

  @ViewChild('walletLabelTpl', {read: TemplateRef})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  walletLabelTpl!: TemplateRef<any>;

  @ViewChild('walletStatusTpl', {read: TemplateRef})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  walletStatusTpl!: TemplateRef<any>;

  constructor(
    private readonly dialogService: TuiDialogService,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
    private readonly currencyPipe: CurrencyPipe,
  ) {}

  ngOnInit(): void {
    this.loadWallets();
  }

  ngAfterViewInit(): void {
    this.setupWalletColumnsIfReady();
  }

  loadWallets(): void {
    if (!this.actions?.fetchWallets || !this.id) {
      return;
    }

    this.loadingWallets = true;
    this.actions.fetchWallets(this.id).subscribe({
      next: (data: Wallet[]) => {
        this.wallets = data;
        this.loadingWallets = false;
        this.setupWalletColumnsIfReady();
      },
      error: (error: HttpErrorResponse) => {
        this.loadingWallets = false;
        this.showErrorAlert(
          error.error?.message ??
            this.translate.instant('common.errorLoadingData'),
        );
      },
    });
  }

  private setupWalletColumnsIfReady(): void {
    if (!this.walletBindingTpl || !this.walletStatusTpl) {
      return;
    }
    this.setupWalletColumns();
  }

  private setupWalletColumns(): void {
    const currencyArgs = ['BRL', 'symbol', '1.2-2', 'pt-BR'] as const;

    this.walletColumns = [
      {
        label: 'common.table.columns.id',
        field: 'id',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.wallets.binding.title',
        field: 'binding',
        sortable: false,
        visible: true,
        alwaysVisible: true,
        template: this.walletBindingTpl,
      },
      {
        label: 'features.wallets.label',
        field: 'label',
        sortable: false,
        visible: true,
        template: this.walletLabelTpl,
      },
      {
        label: 'features.wallets.account',
        field: 'account',
        sortable: false,
        visible: true,
        template: this.walletAccountTpl,
      },
      {
        label: 'features.wallets.availableBalance',
        field: 'availableBalance',
        sortable: false,
        visible: true,
        class: 'gw-tabular-nums',
        pipe: {
          fn: this.currencyPipe,
          args: [...currencyArgs],
        },
      },
      {
        label: 'features.wallets.pendingBalance',
        field: 'pendingBalance',
        sortable: false,
        visible: true,
        class: 'gw-tabular-nums',
        pipe: {
          fn: this.currencyPipe,
          args: [...currencyArgs],
        },
      },
      {
        label: 'features.wallets.withheldBalance',
        field: 'withheldBalance',
        sortable: false,
        visible: true,
        class: 'gw-tabular-nums',
        pipe: {
          fn: this.currencyPipe,
          args: [...currencyArgs],
        },
      },
      {
        label: 'common.table.columns.status',
        field: 'status',
        sortable: false,
        visible: true,
        alwaysVisible: true,
        template: this.walletStatusTpl,
      },
      {
        label: 'common.table.columns.actions',
        field: 'actions',
        sortable: false,
        visible: true,
        alwaysVisible: true,
        actions: [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: '@tui.eye',
          },
          {
            key: 'syncBalance',
            label: 'features.wallets.syncBalance',
            disabled: (row: Wallet) => this.walletSyncingId === row.id,
          },
          {
            key: 'setPrimary',
            label: 'features.wallets.setPrimary',
            condition: {isPrimary: false},
          },
        ],
      },
    ];
  }

  onWalletActionClicked(event: {row: Wallet; type: string}): void {
    switch (event.type) {
      case 'view':
        this.openWalletView(event.row);
        break;
      case 'syncBalance':
        this.syncWalletBalance(event.row.id);
        break;
      case 'setPrimary':
        this.setWalletPrimary(event.row.id);
        break;
      default:
        break;
    }
  }

  openWalletView(wallet: Wallet): void {
    this.dialogService
      .open<boolean>(new PolymorpheusComponent(WalletViewComponent), {
        data: {
          wallet,
          syncWalletFn: (walletId: number) => this.actions.syncWallet(walletId),
          setPrimaryFn: (walletId: number) => this.actions.setPrimary(walletId),
          routingRuleActions: this.actions.integrationRouting,
          associateIntegrationFn: this.actions.associateIntegration
            ? (walletId: number, payload: AssociateWalletPayload) =>
                this.actions.associateIntegration!(walletId, payload)
            : undefined,
          fetchIntegrationsFn: (entityId: number, inUse?: boolean) =>
            this.actions.fetchIntegrations(entityId, inUse),
          fetchPoolsFn: () => this.actions.fetchPools(),
          recipientId: this.id,
        },
        size: 'l',
        label: this.translate.instant('features.wallets.view.title', {
          id: wallet.id,
        }),
        dismissible: true,
        closeable: true,
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.loadWallets();
        }
      });
  }

  syncWalletBalance(walletId: number): void {
    this.walletSyncingId = walletId;
    this.actions.syncWallet(walletId).subscribe({
      next: () => {
        this.walletSyncingId = null;
        this.showSuccessAlert(
          this.translate.instant('features.wallets.syncSuccess'),
        );
        this.loadWallets();
      },
      error: (error: HttpErrorResponse) => {
        this.walletSyncingId = null;
        this.showErrorAlert(
          error.error?.message ??
            this.translate.instant('features.wallets.syncError'),
        );
      },
    });
  }

  setWalletPrimary(walletId: number): void {
    this.actions.setPrimary(walletId).subscribe({
      next: () => {
        this.showSuccessAlert(
          this.translate.instant('features.wallets.setPrimarySuccess'),
        );
        this.loadWallets();
      },
      error: (error: HttpErrorResponse) => {
        this.showErrorAlert(
          error.error?.message ??
            this.translate.instant('features.wallets.setPrimaryError'),
        );
      },
    });
  }

  openCreateWalletDialog(): void {
    this.dialogService
      .open<boolean>(new PolymorpheusComponent(WalletFormComponent), {
        data: {
          recipientId: this.id,
          hasExistingWallets: this.wallets.length > 0,
          defaultBindingMode: 'standalone',
          fetchIntegrationsFn: (entityId: number, inUse?: boolean) =>
            this.actions.fetchIntegrations(entityId, inUse),
          fetchPoolsFn: () => this.actions.fetchPools(),
          createWalletFn: (payload: CreateWalletPayload) =>
            this.actions.createWallet(payload),
        },
        size: 'm',
        label: this.translate.instant('features.wallets.create.title'),
        dismissible: true,
        closeable: true,
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.loadWallets();
        }
      });
  }

  private showSuccessAlert(message: string): void {
    this.alerts
      .open(message, {
        label: this.translate.instant('common.status.success'),
        appearance: 'success',
      })
      .subscribe();
  }

  private showErrorAlert(message: string): void {
    this.alerts
      .open(message, {
        label: this.translate.instant('common.status.error'),
        appearance: 'error',
      })
      .subscribe();
  }
}
